/* Redo HSA/FSA cart badge — UpCart (AfterSell) drawer integration for Perk. v6
 *
 * v6: the check icon is now a CSS-styled text glyph ("✓") matching the
 * perk-sub-benefits list style, instead of the Redo SVG <img> — same
 * mechanism as the theme's benefit checkmarks (generated content, no image
 * request). Set CHECK_COLOR below to Perk's exact green: on a page with the
 * benefits list, run
 *   getComputedStyle(document.querySelector('.perk-sub-benefits li'), '::before').color
 * and paste the result.
 *
 * v5: single cart-level badge below the CHECKOUT button (was: per-line-item).
 * Aggregation rules, since a footer badge describes the whole cart:
 *   - shown when at least one cart item resolves eligible / eligible_with_lmn
 *   - copy "HSA/FSA accepted." if ANY item is plain `eligible`
 *   - copy "HSA/FSA eligible for qualified customers." when the only eligible
 *     items are `eligible_with_lmn`
 *   - hidden when no items are eligible (or the drawer is closed/unmounted)
 * Eligibility per item is read from the tiles currently mounted in the drawer
 * (UpCart keeps those authoritative), so removals/additions recompute the
 * badge immediately via the mutation observers.
 *
 * v3/v4 infrastructure retained: the drawer renders inside an isolation
 * boundary (shadow root or same-origin iframe), so this script discovers the
 * cart's real root (top document, any open shadow root, or any same-origin
 * iframe), attaches a MutationObserver inside that boundary, and injects
 * there. A 2s safety poll covers anything the observers miss. Badge styling
 * is inline, so shadow-DOM style isolation doesn't affect it.
 *
 * DATA SOURCE: theme section sections/redo-hsa-cart-data.liquid, fetched via
 * the Section Rendering API (GET {root}?sections=redo-hsa-cart-data). The
 * section resolves the same metafield hierarchy as the PDP widget for every
 * cart item, server-side, and emits a {variantId: eligibility} JSON map.
 * Each fetch REPLACES the map (the section always reflects the full current
 * cart), so removed items can't leave stale eligibility behind.
 *
 * INSTALL (unchanged):
 *   1. sections/redo-hsa-cart-data.liquid (already in place).
 *   2. This file as assets/redo-hsa-cart-badge.js (replace previous version).
 *   3. In layout/theme.liquid, just before </body>:
 *        <script src="{{ 'redo-hsa-cart-badge.js' | asset_url }}" defer="defer"></script>
 */
(() => {
  if (window.__redoHsaCartBadgeActive) return; /* run-once guard */
  window.__redoHsaCartBadgeActive = true;

  const TILE_SELECTOR = ".upcart-internal-component-product-tile";
  const CHECKOUT_SELECTOR = ".upcart-checkout-button-container";
  const FOOTER_SELECTOR = ".upcart-footer";
  const CART_CONTAINER_SELECTOR = ".upcart-cart, #CartPopup, .upcart-internal-cart-preview__container";
  const BADGE_CLASS = "redo-hsa-cart-badge";
  const SECTION_ID = "redo-hsa-cart-data";
  const FETCH_COOLDOWN_MS = 4000;
  const POLL_MS = 2000;
  const CHECK_GLYPH = "✓"; /* ✓ */
  const CHECK_COLOR = "#2e7d32"; /* eyeball match — replace with Perk's exact green (see header) */
  const COPY = {
    eligible: "HSA/FSA accepted.",
    eligible_with_lmn: "HSA/FSA eligible for qualified customers.",
  };

  let map = {};
  let fetchInFlight = null;
  let lastFetchAt = 0;
  let noProgressFetches = 0;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function fetchMap() {
    if (fetchInFlight) return fetchInFlight;
    if (noProgressFetches >= 3) return Promise.resolve(map);
    const now = Date.now();
    if (now - lastFetchAt < FETCH_COOLDOWN_MS) return Promise.resolve(map);
    lastFetchAt = now;
    fetchInFlight = fetch(rootUrl() + "?sections=" + SECTION_ID)
      .then((r) => r.json())
      .then((json) => {
        const html = json && json[SECTION_ID];
        if (!html) {
          noProgressFetches += 1;
          return map;
        }
        const doc = new DOMParser().parseFromString(html, "text/html");
        const el = doc.querySelector("[data-redo-hsa-cart-map]");
        if (!el) {
          noProgressFetches += 1;
          return map;
        }
        const next = JSON.parse(el.textContent);
        const changed = JSON.stringify(next) !== JSON.stringify(map);
        map = next;
        noProgressFetches = changed ? 0 : noProgressFetches + 1;
        return map;
      })
      .catch((err) => {
        console.error("Redo HSA cart badge: map fetch failed", err);
        noProgressFetches += 1;
        return map;
      })
      .finally(() => {
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  /* ---- root discovery across isolation boundaries ---- */

  let cartRoots = []; /* cached Document / ShadowRoot nodes containing UpCart DOM */
  const observedRoots = new WeakSet();

  /* Recursively collect the top document, every open shadow root, and every
     same-origin iframe document (cross-origin iframes throw and are skipped). */
  function collectRoots(root, acc) {
    acc.push(root);
    let all;
    try {
      all = root.querySelectorAll("*");
    } catch (e) {
      return;
    }
    for (const el of all) {
      if (el.shadowRoot) collectRoots(el.shadowRoot, acc);
      if (el.tagName === "IFRAME") {
        try {
          if (el.contentDocument) collectRoots(el.contentDocument, acc);
        } catch (e) {
          /* cross-origin iframe — unreachable, skip */
        }
      }
    }
  }

  function rootIsAlive(r) {
    if (r === document) return true;
    if (r.nodeType === 9) {
      try {
        const frame = r.defaultView && r.defaultView.frameElement;
        return !!(frame && frame.isConnected);
      } catch (e) {
        return false;
      }
    }
    return !!(r.host && r.host.isConnected);
  }

  function observeRoot(r) {
    if (observedRoots.has(r)) return;
    try {
      const target = r.nodeType === 9 ? r.documentElement || r : r;
      new MutationObserver(schedule).observe(target, { childList: true, subtree: true });
      observedRoots.add(r);
    } catch (e) {
      /* leave unobserved; the safety poll still covers this root */
    }
  }

  function ensureRoots() {
    cartRoots = cartRoots.filter(rootIsAlive);
    if (cartRoots.length === 0) {
      const acc = [];
      collectRoots(document, acc);
      cartRoots = acc.filter((r) => {
        try {
          return r.querySelector(TILE_SELECTOR) || r.querySelector(CART_CONTAINER_SELECTOR);
        } catch (e) {
          return false;
        }
      });
      cartRoots.forEach(observeRoot);
    }
    return cartRoots;
  }

  /* ---- badge rendering ---- */

  function variantIdFor(tile) {
    const link = tile.querySelector("a[href*='variant=']");
    if (link) {
      const match = link.getAttribute("href").match(/[?&]variant=(\d+)/);
      if (match) return match[1];
    }
    return null;
  }

  function makeBadge(copyKey, ownerDoc) {
    const doc = ownerDoc || document;
    const badge = doc.createElement("div");
    badge.className = BADGE_CLASS;
    badge.setAttribute("data-redo-hsa-eligibility", copyKey);
    badge.style.cssText =
      "display:flex;align-items:flex-start;justify-content:center;gap:5px;" +
      "margin:8px 0 0;font-family:'Inter',Arial,sans-serif;font-size:13px;" +
      "font-weight:500;line-height:1.4;color:#000;";

    const icon = doc.createElement("span");
    icon.textContent = CHECK_GLYPH;
    icon.setAttribute("aria-hidden", "true");
    icon.style.cssText =
      "color:" + CHECK_COLOR + ";font-weight:700;font-size:14px;" +
      "line-height:inherit;flex-shrink:0;";

    const text = doc.createElement("span");
    text.textContent = COPY[copyKey];

    badge.appendChild(icon);
    badge.appendChild(text);
    return badge;
  }

  /* Idempotent: safe to run on every DOM change; settles with no mutations. */
  function apply() {
    if (window.Shopify && window.Shopify.country && window.Shopify.country !== "US") {
      return;
    }
    let sawUnknownVariant = false;
    for (const root of ensureRoots()) {
      let tiles;
      try {
        tiles = root.querySelectorAll(TILE_SELECTOR);
      } catch (e) {
        continue;
      }

      /* Aggregate eligibility across the items currently in the drawer. */
      const statuses = [];
      tiles.forEach((tile) => {
        const id = variantIdFor(tile);
        if (!id) return;
        const eligibility = map[id];
        if (eligibility === undefined) sawUnknownVariant = true;
        else statuses.push(eligibility);
      });
      const eligibleStatuses = statuses.filter(
        (s) => s === "eligible" || s === "eligible_with_lmn"
      );

      const existing = root.querySelector("." + BADGE_CLASS);
      const checkout = root.querySelector(CHECKOUT_SELECTOR);
      const footer = root.querySelector(FOOTER_SELECTOR);
      const anchor = checkout || footer;

      if (eligibleStatuses.length === 0 || !anchor) {
        if (existing) existing.remove();
        continue;
      }

      const copyKey = eligibleStatuses.some((s) => s === "eligible")
        ? "eligible"
        : "eligible_with_lmn";

      let badge = existing;
      if (!badge) {
        badge = makeBadge(copyKey, anchor.ownerDocument);
      } else if (badge.getAttribute("data-redo-hsa-eligibility") !== copyKey) {
        const replacement = makeBadge(copyKey, anchor.ownerDocument);
        badge.replaceWith(replacement);
        badge = replacement;
      }

      /* Below the checkout button when it exists; end of the footer otherwise.
         Position check keeps this idempotent across UpCart re-renders. */
      if (checkout) {
        if (badge.previousElementSibling !== checkout) {
          checkout.insertAdjacentElement("afterend", badge);
        }
      } else if (badge.parentElement !== footer || badge.nextElementSibling !== null) {
        footer.appendChild(badge);
      }
    }
    if (sawUnknownVariant) {
      fetchMap().then(schedule);
    }
  }

  /* Coalesce observer bursts into one apply() per frame; apply() is
     idempotent so self-triggered mutations settle immediately. */
  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      apply();
    });
  }

  function init() {
    observeRoot(document);
    setInterval(schedule, POLL_MS);
    fetchMap().then(schedule);
  }

  /* Debugging helpers. */
  window.redoHsaCartBadgeRefresh = function () {
    lastFetchAt = 0;
    noProgressFetches = 0;
    cartRoots = [];
    fetchMap().then(schedule);
  };
  window.redoHsaCartBadgeDebug = function () {
    const roots = ensureRoots();
    return {
      map: map,
      roots: roots.map((r) =>
        r === document ? "top-document" : r.nodeType === 9 ? "iframe-document" : "shadow-root(" + (r.host && r.host.tagName) + "#" + (r.host && r.host.id) + ")"
      ),
      tiles: roots.reduce((n, r) => n + r.querySelectorAll(TILE_SELECTOR).length, 0),
      badges: roots.reduce((n, r) => n + r.querySelectorAll("." + BADGE_CLASS).length, 0),
    };
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();